import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Hotspot {
  id: string;
  label: string;
  description: string;
  top_percent: number;
  left_percent: number;
  tooltip_position: string;
  is_active: boolean;
  position: number;
}

export const useHotspots = () => {
  const [hotspots, setHotspots] = useState<Hotspot[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchHotspots = async () => {
    const { data, error } = await supabase
      .from('hero_hotspots')
      .select('*')
      .order('position', { ascending: true });

    if (error) {
      console.error('Error fetching hotspots:', error);
    } else {
      setHotspots(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchHotspots();
  }, []);

  const addHotspot = async (hotspot: Omit<Hotspot, 'id' | 'is_active' | 'position'>) => {
    const maxPosition = hotspots.length > 0 ? Math.max(...hotspots.map(h => h.position)) : 0;
    
    const { data, error } = await supabase
      .from('hero_hotspots')
      .insert({
        ...hotspot,
        is_active: true,
        position: maxPosition + 1,
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding hotspot:', error);
      throw error;
    }

    setHotspots([...hotspots, data]);
    return data;
  };

  const updateHotspot = async (id: string, updates: Partial<Hotspot>) => {
    const { error } = await supabase
      .from('hero_hotspots')
      .update(updates)
      .eq('id', id);

    if (error) {
      console.error('Error updating hotspot:', error);
      throw error;
    }

    setHotspots(hotspots.map(h => h.id === id ? { ...h, ...updates } : h));
  };

  const deleteHotspot = async (id: string) => {
    const { error } = await supabase
      .from('hero_hotspots')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting hotspot:', error);
      throw error;
    }

    setHotspots(hotspots.filter(h => h.id !== id));
  };

  return {
    hotspots,
    loading,
    refetch: fetchHotspots,
    addHotspot,
    updateHotspot,
    deleteHotspot,
  };
};
